import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const label = (c) => c.replace(/_/g, ' ');

export const PlaceholderChartsPanel = ({ onChanged }) => {
  const [charts, setCharts] = useState([]);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);
  const load = () => api.get('/chart-cleanup/placeholders').then(r => setCharts(r.data.charts || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    const what = c.recordCount ? `${c.recordCount} auto-generated record(s) filed in it` : 'no records';
    if (!window.confirm(`Delete this "${c.firstName} ${c.lastName}" chart and ${what}? This cannot be undone.`)) return;
    setBusy(c.id);
    try {
      await api.delete(`/chart-cleanup/placeholders/${c.id}`);
      toast.success('Placeholder chart deleted');
      setCharts(list => list.filter(x => x.id !== c.id));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    } finally {
      setBusy('');
    }
  };

  if (!charts.length) return null;
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-lg p-4" data-testid="placeholder-charts-panel">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="w-full flex items-start gap-2 text-left" data-testid="placeholder-charts-toggle">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{charts.length} chart(s) without a real patient name</h3>
          <p className="text-sm text-gray-600">Created by uploads that had no patient name. Review what was filed in each, then delete the ones you don't need. New uploads without a name no longer create charts.</p>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-600 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-2 mt-3">
        {charts.map(c => (
          <div key={c.id} className="bg-white border rounded-md p-3 flex items-center gap-3 justify-between" data-testid={`placeholder-chart-${c.id}`}>
            <div className="text-sm min-w-0 flex-1">
              <p className="font-medium">{c.firstName} {c.lastName} <span className="text-gray-400 font-normal">#{c.id.slice(0, 8)}</span></p>
              <p className="text-xs text-gray-500">
                {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}{c.sourceDocument ? ` from ${c.sourceDocument}` : ''}
                {' '}| {c.recordCount ? Object.entries(c.records).map(([k, n]) => `${label(k)} (${n})`).join(', ') : 'empty'}
              </p>
            </div>
            <button onClick={() => remove(c)} disabled={busy === c.id} className="shrink-0 px-3 py-1.5 text-sm rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1 disabled:opacity-50" data-testid={`placeholder-delete-${c.id}`}>
              {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
            </button>
          </div>
        ))}
      </div>}
    </div>
  );
};
